import React from "react";

import {
  listConversations,
  markConversationRead,
  markConversationUnread,
  TCommunicationsConversation,
} from "../../../../../services/api-communications";
import { TSession } from "../../../../../utils/types";
import { toUserFacingErrorMessage } from "../utils/errors";

type UseMessagingConversationsParams = {
  session: TSession | null | undefined;
  enabled?: boolean;
  pollIntervalMs?: number;
};

type TRefreshOptions = {
  silent?: boolean;
};

type TMarkConversationOptions = {
  silentRefresh?: boolean;
  optimistic?: boolean;
};

const toConversationReadState = (
  conversation: TCommunicationsConversation,
): { isRead: boolean; unreadCount: number } => {
  const normalizedUnreadCount = Math.max(conversation.unread_count || 0, 0);
  const isRead =
    typeof conversation.is_read === "boolean"
      ? conversation.is_read
      : normalizedUnreadCount <= 0;
  return {
    isRead,
    unreadCount: normalizedUnreadCount || (isRead ? 0 : 1),
  };
};

export const useMessagingConversations = ({
  session,
  enabled = true,
  pollIntervalMs = 4000,
}: UseMessagingConversationsParams) => {
  const [conversations, setConversations] = React.useState<
    TCommunicationsConversation[]
  >([]);
  const [total, setTotal] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedConversationUuid, setSelectedConversationUuid] =
    React.useState<string | null>(null);

  const refresh = React.useCallback(
    async ({ silent = false }: TRefreshOptions = {}) => {
      if (!session || !enabled) {
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await listConversations(session, {
          limit: 100,
          offset: 0,
        });

        const nextResults = response.results || [];
        setConversations(nextResults);
        setTotal(
          typeof response.total === "number"
            ? response.total
            : nextResults.length,
        );
      } catch (nextError) {
        setError(
          toUserFacingErrorMessage(
            nextError,
            "Unable to load message conversations.",
          ),
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [enabled, session],
  );

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    refresh().catch(() => undefined);
  }, [enabled, refresh]);

  React.useEffect(() => {
    if (!enabled || !session) {
      return;
    }

    const intervalId = setInterval(
      () => {
        refresh({ silent: true }).catch(() => undefined);
      },
      Math.max(pollIntervalMs, 2000),
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [enabled, pollIntervalMs, refresh, session]);

  React.useEffect(() => {
    if (!selectedConversationUuid) {
      return;
    }

    const selectionStillExists = conversations.some(
      (item) => item.conversation_uuid === selectedConversationUuid,
    );
    if (!selectionStillExists) {
      setSelectedConversationUuid(null);
    }
  }, [conversations, selectedConversationUuid]);

  React.useEffect(() => {
    if (session) {
      return;
    }

    setConversations([]);
    setTotal(0);
    setError(null);
    setLoading(false);
    setSelectedConversationUuid(null);
  }, [session]);

  const markRead = React.useCallback(
    async (
      conversationUuid: string,
      options: TMarkConversationOptions = {},
    ): Promise<void> => {
      if (!session || !conversationUuid) {
        return;
      }

      const { optimistic = true, silentRefresh = true } = options;

      if (optimistic) {
        const nowIso = new Date().toISOString();
        setConversations((currentConversations) =>
          currentConversations.map((conversation) => {
            if (conversation.conversation_uuid !== conversationUuid) {
              return conversation;
            }
            const state = toConversationReadState(conversation);
            if (state.isRead && state.unreadCount <= 0) {
              return conversation;
            }
            return {
              ...conversation,
              is_read: true,
              read_at: nowIso,
              unread_count: 0,
            };
          }),
        );
      }

      await markConversationRead(session, conversationUuid);
      refresh({ silent: silentRefresh }).catch(() => undefined);
    },
    [refresh, session],
  );

  const markUnread = React.useCallback(
    async (
      conversationUuid: string,
      options: TMarkConversationOptions = {},
    ): Promise<void> => {
      if (!session || !conversationUuid) {
        return;
      }

      const { optimistic = true, silentRefresh = true } = options;

      if (optimistic) {
        setConversations((currentConversations) =>
          currentConversations.map((conversation) => {
            if (conversation.conversation_uuid !== conversationUuid) {
              return conversation;
            }
            const state = toConversationReadState(conversation);
            if (!state.isRead) {
              return conversation;
            }
            return {
              ...conversation,
              is_read: false,
              read_at: null,
              unread_count: Math.max(state.unreadCount, 1),
            };
          }),
        );
      }

      await markConversationUnread(session, conversationUuid);
      refresh({ silent: silentRefresh }).catch(() => undefined);
    },
    [refresh, session],
  );

  return {
    conversations,
    total,
    loading,
    error,
    refresh,
    markRead,
    markUnread,
    selectedConversationUuid,
    setSelectedConversationUuid,
  };
};

export default useMessagingConversations;
